import React, { useState, useEffect } from "react";

// =========================
// Local Image Imports (all .jpeg)
// =========================
import pest1 from "../../../assets/images/pest1.jpeg";
import pest2 from "../../../assets/images/pest2.jpeg";
import pest3 from "../../../assets/images/pest3.jpeg";

import cleaning1 from "../../../assets/images/cleaning1.jpeg";
import cleaning2 from "../../../assets/images/cleaning2.jpeg";
import cleaning3 from "../../../assets/images/cleaning3.jpeg";

import agri1 from "../../../assets/images/agri1.jpeg";
import agr2 from "../../../assets/images/agr2.jpeg";

import termite1 from "../../../assets/images/termite1.jpeg";
import termite2 from "../../../assets/images/termite2.jpeg";
import termite3 from "../../../assets/images/termite3.jpeg";

import mosquito1 from "../../../assets/images/mosquito1.jpeg";
import mosquito2 from "../../../assets/images/mosquito2.jpeg";
import mosquito3 from "../../../assets/images/mosquito3.jpeg";

import bees1 from "../../../assets/images/bees1.jpeg";
import bees2 from "../../../assets/images/bees2.jpeg";
import bees3 from "../../../assets/images/bees3.jpeg";

const ServicesSection = () => {
  const [currentImageIndexes, setCurrentImageIndexes] = useState({});
  const [selectedService, setSelectedService] = useState(null);

  const services = [
    {
      id: 1,
      title: "Pest Control",
      description: "Complete protection for your home and family from common household pests.",
      detailedDescription:
        "Our  pest control services provide comprehensive protection against ants, cockroaches, rodents, spiders, bed bugs, and more. We use eco-friendly products and tailored treatment plans to ensure your home remains safe and pest-free all year round.",
      features: ["Ants & Cockroaches", "Rodents", "Spiders", "Bed Bugs"],
      images: [pest1, pest2, pest3],
    },
    {
      id: 2,
      title: "Cleaning Services",
      description: "Protect your business and maintain compliance with our cleaning services.",
      detailedDescription:
        "Our commercial cleaning and pest management services are designed for restaurants, offices, warehouses, and retail stores. We help you maintain a spotless environment and meet health regulations with regular inspections and treatments.",
      features: ["Restaurants", "Offices", "Warehouses", "Retail Stores"],
      images: [cleaning1, cleaning2, cleaning3],
    },
    {
      id: 3,
      title: "Landscaping & Grass Cutting",
      description: "Professional lawn maintenance and landscape design for residential and commercial properties.",
      detailedDescription:
        "Our landscaping and grass cutting services keep your outdoor spaces pristine. We offer regular mowing, edging, trimming, and full landscape design including garden beds, hardscaping, and irrigation. Whether you need weekly maintenance or a complete transformation, our team delivers excellence.",
      features: ["Lawn Mowing & Edging", "Garden Design", "Hedge Trimming", "Seasonal Cleanup"],
      images: [agri1, agr2, agri1],
    },
    {
      id: 4,
      title: "Bee Removal & Emergency",
      description: "Safe and humane bee removal, plus 24/7 emergency pest control for urgent situations.",
      detailedDescription:
        "Our bee removal service is humane and eco-friendly, relocating hives without harming the bees. We also offer 24/7 emergency response for any pest crisis, ensuring rapid intervention when you need it most.",
      features: ["Bee Hive Removal", "Wasp Control", "24/7 Service", "Rapid Response"],
      images: [bees1, bees2, bees3],
    },
    {
      id: 5,
      title: "Termite Control",
      description: "Professional termite inspection, treatment, and prevention services.",
      detailedDescription:
        "Termites can cause severe structural damage. Our termite control includes thorough inspections, wood and soil treatments, and long-term prevention plans to protect your property from these silent destroyers.",
      features: ["Termite Inspection", "Wood Treatment", "Soil Treatment", "Prevention"],
      images: [termite1, termite2, termite3],
    },
    {
      id: 6,
      title: "Mosquito & Fly Management",
      description: "Effective control of flying insects for comfortable outdoor and indoor spaces.",
      detailedDescription:
        "Our mosquito and fly management programs target breeding sites and adult populations using safe sprays and larvicides. We provide both one-time treatments and seasonal plans to keep your outdoor areas enjoyable.",
      features: ["Mosquito Control", "Fly Elimination", "Outdoor Treatment", "Preventive Sprays"],
      images: [mosquito1, mosquito2, mosquito3],
    },
  ];

  // Initialize current image indexes
  useEffect(() => {
    const initialIndexes = {};
    services.forEach(service => {
      initialIndexes[service.id] = 0;
    });
    setCurrentImageIndexes(initialIndexes);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-rotate images for each service with random timing
  useEffect(() => {
    const intervals = services.map(service => {
      const randomInterval = 3000 + Math.random() * 5000;
      return setInterval(() => {
        setCurrentImageIndexes(prev => ({
          ...prev,
          [service.id]: (prev[service.id] + 1) % service.images.length
        }));
      }, randomInterval);
    });

    return () => intervals.forEach(interval => clearInterval(interval));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDotClick = (serviceId, index) => {
    setCurrentImageIndexes(prev => ({
      ...prev,
      [serviceId]: index
    }));
  };

  const openModal = (service) => setSelectedService(service);
  const closeModal = () => setSelectedService(null);

  // Close modal on Escape key
  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === "Escape") closeModal();
    };
    window.addEventListener("keydown", handleEsc);
    return () => window.removeEventListener("keydown", handleEsc);
  }, []);

  return (
    <section style={styles.container}>
      <div style={styles.header}>
        <h2 style={styles.title}>Our Premium Services</h2>
      </div>

      <div style={styles.servicesGrid}>
        {services.map((service) => (
          <div key={service.id} style={styles.serviceCard}>
            {/* Image Carousel Container */}
            <div style={styles.imageCarousel}>
              {service.images.map((image, imgIndex) => (
                <div
                  key={imgIndex}
                  style={{
                    ...styles.carouselImage,
                    opacity: imgIndex === currentImageIndexes[service.id] ? 1 : 0,
                    transform: `scale(${imgIndex === currentImageIndexes[service.id] ? 1 : 1.05})`,
                    zIndex: imgIndex === currentImageIndexes[service.id] ? 1 : 0,
                  }}
                >
                  <img 
                    src={image} 
                    alt={`${service.title} ${imgIndex + 1}`}
                    style={styles.image}
                  />
                  <div style={styles.imageOverlay} />
                </div>
              ))}
              
              {/* Image Navigation Dots */}
              <div style={styles.carouselDots}>
                {service.images.map((_, dotIndex) => (
                  <button
                    key={dotIndex}
                    style={{
                      ...styles.dot,
                      background: dotIndex === currentImageIndexes[service.id] 
                        ? '#3498db' 
                        : 'rgba(255,255,255,0.6)'
                    }}
                    onClick={() => handleDotClick(service.id, dotIndex)}
                  />
                ))}
              </div>
            </div>

            {/* Card Content */}
            <div style={styles.cardContent}>
              <h3 style={styles.serviceTitle}>{service.title}</h3>
              <p style={styles.serviceDescription}>{service.description}</p>
              
              <div style={styles.features}>
                {service.features.map((feature, featureIndex) => (
                  <div key={featureIndex} style={styles.featureItem}>
                    <div style={styles.featureDot}></div>
                    <span style={styles.featureText}>{feature}</span>
                  </div>
                ))}
              </div>

              <div style={styles.cardFooter}>
                <button 
                  style={styles.learnMoreButton}
                  onClick={() => openModal(service)}
                >
                  <span>Learn More</span>
                  <div style={styles.buttonIcon}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                      <path d="M5 12H19M19 12L12 5M19 12L12 19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Bottom CTA */}
      <div style={styles.bottomCTA}>
        <div style={styles.ctaContent}>
          <h3 style={styles.ctaTitle}>Need Immediate Assistance?</h3>
          <p style={styles.ctaText}>Our team is available 24/7 for emergency pest control services</p>
          {/* Changed from button to anchor tag with tel: href */}
          <a
            href="tel:+27113940990"
            style={styles.emergencyButton}
            className="emergency-btn"
          >
            <span style={styles.emergencyIcon}>🚨</span>
            Emergency Call: +27 11 394 0990
          </a>
        </div>
      </div>

      {/* Service Detail Modal */}
      {selectedService && (
        <div style={styles.modalOverlay} className="modal-overlay" onClick={closeModal}>
          <div style={styles.modalContent} className="modal-content" onClick={(e) => e.stopPropagation()}>
            <button style={styles.modalCloseButton} className="modal-close-button" onClick={closeModal}>×</button>
            <h3 style={styles.modalTitle} className="modal-title">{selectedService.title}</h3>
            <p style={styles.modalDescription} className="modal-description">{selectedService.detailedDescription}</p>
            <div style={styles.modalFeatures} className="modal-features">
              <h4 style={styles.modalFeaturesTitle} className="modal-features-title">Key Features:</h4>
              <ul style={styles.modalFeaturesList} className="modal-features-list">
                {selectedService.features.map((feat, idx) => (
                  <li key={idx} style={styles.modalFeatureItem} className="modal-feature-item">{feat}</li>
                ))}
              </ul>
            </div>
            <button style={styles.modalActionButton} className="modal-action-button" onClick={closeModal}>
              Close
            </button>
          </div>
        </div>
      )}
    </section>
  );
};

export default ServicesSection;

/* =========================
   REDUCED TOP PADDING ONLY
   ========================= */
const styles = {
  container: {
    position: "relative",
    padding: "1rem 1rem", // Reduced from 5rem to 3rem (top/bottom)
    background: "#ffffff",
    overflow: "hidden",
    fontFamily: 'system-ui, -apple-system, "Segoe UI", Roboto, sans-serif',
  },

  header: {
    textAlign: "center",
    marginBottom: "2.5rem", // Reduced from 4rem to 2.5rem
    position: "relative",
  },

  title: {
    fontSize: "clamp(2.5rem, 5vw, 3.5rem)",
    fontWeight: "bold",
    background: "linear-gradient(135deg, #2c3e50 0%, #3498db 100%)",
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
    backgroundClip: "text",
    margin: "0 0 1rem 0",
    letterSpacing: "-0.02em",
  },

  subtitle: {
    fontSize: "clamp(1rem, 2vw, 1.2rem)",
    color: "#6c757d",
    maxWidth: "600px",
    margin: "0 auto",
    lineHeight: 1.6,
  },

  servicesGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
    gap: "1.5rem",
    maxWidth: "1400px",
    margin: "0 auto 4rem",
  },

  serviceCard: {
    position: "relative",
    background: "#ffffff",
    borderRadius: "20px",
    overflow: "hidden",
    border: "1px solid #e9ecef",
    transition: "all 0.3s ease",
    boxShadow: "0 4px 20px rgba(0, 0, 0, 0.08)",
  },

  imageCarousel: {
    position: "relative",
    height: "220px", // Kept original height
    overflow: "hidden",
  },

  carouselImage: {
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    transition: 'all 1.2s cubic-bezier(0.25, 0.46, 0.45, 0.94)'
  },

  image: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
  },

  imageOverlay: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: "linear-gradient(135deg, rgba(44, 62, 80, 0.7) 0%, rgba(52, 152, 219, 0.6) 100%)",
  },

  serviceIcon: {
    position: "absolute",
    top: "1rem",
    left: "1rem",
    width: "45px",
    height: "45px",
    background: "rgba(255, 255, 255, 0.95)",
    borderRadius: "10px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    boxShadow: "0 4px 15px rgba(0, 0, 0, 0.15)",
  },

  icon: {
    fontSize: "1.3rem",
  },

  carouselDots: {
    position: "absolute",
    bottom: "0.8rem",
    left: "50%",
    transform: "translateX(-50%)",
    display: "flex",
    gap: "0.4rem",
  },

  dot: {
    width: "6px",
    height: "6px",
    borderRadius: "50%",
    border: "none",
    cursor: "pointer",
    transition: "all 0.3s ease",
  },

  cardContent: {
    padding: "1.5rem",
  },

  serviceTitle: {
    fontSize: "1.3rem",
    fontWeight: "bold",
    color: "#2c3e50",
    margin: "0 0 0.8rem 0",
  },

  serviceDescription: {
    color: "#6c757d",
    lineHeight: 1.5,
    margin: "0 0 1.2rem 0",
    fontSize: "0.9rem",
  },

  features: {
    display: "flex",
    flexDirection: "column",
    gap: "0.6rem",
    marginBottom: "1.5rem",
  },

  featureItem: {
    display: "flex",
    alignItems: "center",
    gap: "0.6rem",
  },

  featureDot: {
    width: "5px",
    height: "5px",
    borderRadius: "50%",
    background: "linear-gradient(135deg, #3498db, #2ecc71)",
    flexShrink: 0,
  },

  featureText: {
    color: "#495057",
    fontSize: "0.85rem",
    fontWeight: "500",
  },

  cardFooter: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-start",
  },

  learnMoreButton: {
    display: "flex",
    alignItems: "center",
    gap: "0.4rem",
    background: "linear-gradient(135deg, #3498db 0%, #2980b9 100%)",
    color: "white",
    border: "none",
    padding: "10px 18px",
    borderRadius: "6px",
    fontWeight: "600",
    cursor: "pointer",
    transition: "all 0.3s ease",
    fontSize: "0.85rem",
    boxShadow: "0 3px 12px rgba(52, 152, 219, 0.3)",
  },

  buttonIcon: {
    transition: "transform 0.3s ease",
  },

  bottomCTA: {
    background: "linear-gradient(135deg, #1a2980 0%, #26d0ce 100%)",
    border: "none",
    borderRadius: "16px",
    padding: "2.5rem 2rem",
    textAlign: "center",
    maxWidth: "800px",
    margin: "0 auto",
    position: "relative",
    overflow: "hidden",
    color: "white",
  },

  ctaContent: {
    maxWidth: "500px",
    margin: "0 auto",
    position: "relative",
    zIndex: 2,
  },

  ctaTitle: {
    fontSize: "1.6rem",
    fontWeight: "bold",
    color: "white",
    margin: "0 0 0.8rem 0",
  },

  ctaText: {
    color: "rgba(255, 255, 255, 0.9)",
    margin: "0 0 1.5rem 0",
    lineHeight: 1.5,
    fontSize: "0.95rem",
    fontWeight: "500",
  },

  emergencyButton: {
    display: "inline-flex",
    alignItems: "center",
    gap: "0.6rem",
    background: "linear-gradient(135deg, #27ae60 0%, #219653 100%)",
    color: "white",
    border: "none",
    padding: "14px 28px",
    borderRadius: "10px",
    fontWeight: "bold",
    cursor: "pointer",
    transition: "all 0.3s ease",
    fontSize: "1rem",
    boxShadow: "0 5px 18px rgba(39, 174, 96, 0.3)",
    textDecoration: "none", // Added to remove underline for anchor
  },

  emergencyIcon: {
    fontSize: "1.2rem",
  },

  // Modal Styles
  modalOverlay: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(0, 0, 0, 0.7)",
    backdropFilter: "blur(5px)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1000,
  },

  modalContent: {
    background: "white",
    borderRadius: "24px",
    padding: "2rem",
    maxWidth: "500px",
    width: "90%",
    maxHeight: "80vh",
    overflowY: "auto",
    position: "relative",
    boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
    border: "1px solid rgba(52, 152, 219, 0.3)",
  },

  modalCloseButton: {
    position: "absolute",
    top: "1rem",
    right: "1rem",
    background: "none",
    border: "none",
    fontSize: "2rem",
    lineHeight: 1,
    cursor: "pointer",
    color: "#6c757d",
    padding: "0.25rem 0.75rem",
    borderRadius: "50%",
    transition: "all 0.2s ease",
  },

  modalTitle: {
    fontSize: "1.8rem",
    fontWeight: "bold",
    marginBottom: "1rem",
    background: "linear-gradient(135deg, #2c3e50 0%, #3498db 100%)",
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
    backgroundClip: "text",
    paddingRight: "2rem",
  },

  modalDescription: {
    fontSize: "1rem",
    lineHeight: 1.6,
    color: "#495057",
    marginBottom: "1.5rem",
  },

  modalFeatures: {
    marginBottom: "2rem",
  },

  modalFeaturesTitle: {
    fontSize: "1.1rem",
    fontWeight: "600",
    color: "#2c3e50",
    marginBottom: "0.75rem",
  },

  modalFeaturesList: {
    listStyle: "none",
    padding: 0,
    margin: 0,
  },

  modalFeatureItem: {
    padding: "0.5rem 0",
    borderBottom: "1px solid #e9ecef",
    color: "#495057",
  },

  modalActionButton: {
    background: "linear-gradient(135deg, #3498db 0%, #2980b9 100%)",
    color: "white",
    border: "none",
    padding: "12px 24px",
    borderRadius: "8px",
    fontWeight: "600",
    fontSize: "1rem",
    cursor: "pointer",
    transition: "all 0.3s ease",
    width: "100%",
    boxShadow: "0 4px 15px rgba(52, 152, 219, 0.3)",
  },
};

// Add CSS with class-based targeting and mobile responsiveness
const styleElement = document.createElement('style');
styleElement.textContent = `
  /* Keyframes */
  @keyframes smoothFade {
    0% { opacity: 0; transform: scale(1.05); }
    20% { opacity: 1; transform: scale(1); }
    80% { opacity: 1; transform: scale(1); }
    100% { opacity: 0; transform: scale(1.05); }
  }

  /* Bottom CTA decorative elements */
  .bottom-cta::before {
    content: "";
    position: absolute;
    top: -50%;
    right: -20%;
    width: 200px;
    height: 200px;
    background: linear-gradient(135deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.05) 100%);
    border-radius: 50%;
    z-index: 1;
  }

  .bottom-cta::after {
    content: "";
    position: absolute;
    bottom: -30%;
    left: -10%;
    width: 150px;
    height: 150px;
    background: linear-gradient(135deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.05) 100%);
    border-radius: 50%;
    z-index: 1;
  }

  /* Hover effects */
  @media (hover: hover) {
    .service-card:hover {
      transform: translateY(-6px);
      box-shadow: 0 10px 25px rgba(0, 0, 0, 0.12);
      border-color: #3498db;
      transition: all 0.4s ease;
    }
    
    .learn-more-btn:hover {
      transform: translateY(-2px);
      box-shadow: 0 5px 15px rgba(52, 152, 219, 0.4);
      transition: all 0.3s ease;
    }
    
    .learn-more-btn:hover .button-icon {
      transform: translateX(2px);
      transition: transform 0.3s ease;
    }
    
    .emergency-btn:hover {
      transform: translateY(-2px);
      box-shadow: 0 6px 20px rgba(39, 174, 96, 0.4);
      background: linear-gradient(135deg, #219653 0%, #1e8749 100%);
      transition: all 0.3s ease;
    }
    
    .dot:hover {
      transform: scale(1.3);
      transition: all 0.3s ease;
    }

    /* Modal hover effects */
    .modal-close-button:hover {
      background: #f1f3f5;
      color: #2c3e50;
    }

    .modal-action-button:hover {
      transform: translateY(-2px);
      box-shadow: 0 6px 20px rgba(52, 152, 219, 0.4);
    }
  }

  /* Mobile responsiveness for modal */
  @media (max-width: 768px) {
    .modal-content {
      padding: 1.5rem !important;
      width: 95% !important;
    }
    .modal-title {
      font-size: 1.5rem !important;
    }
    .modal-close-button {
      padding: 0.5rem 1rem !important;
      font-size: 1.5rem !important;
      top: 0.5rem !important;
      right: 0.5rem !important;
    }
    .modal-description {
      font-size: 0.95rem !important;
    }
    .modal-features-title {
      font-size: 1rem !important;
    }
    .modal-feature-item {
      font-size: 0.9rem !important;
    }
    .modal-action-button {
      padding: 10px 20px !important;
      font-size: 0.95rem !important;
    }
  }

  @media (max-width: 480px) {
    .modal-content {
      padding: 1.2rem !important;
    }
    .modal-title {
      font-size: 1.3rem !important;
    }
    .modal-close-button {
      padding: 0.5rem 0.8rem !important;
      font-size: 1.3rem !important;
    }
  }
`;

// Add class names to elements for CSS targeting (existing ones kept)
const addClassNames = () => {
  if (typeof document !== 'undefined') {
    setTimeout(() => {
      // Existing elements
      const cards = document.querySelectorAll('[style*="border: 1px solid #e9ecef"]');
      cards.forEach(card => card.classList.add('service-card'));
      
      const buttons = document.querySelectorAll('[style*="linear-gradient(135deg, #3498db 0%, #2980b9 100%)"]');
      buttons.forEach(btn => {
        btn.classList.add('learn-more-btn');
        const icon = btn.querySelector('[style*="transition: transform 0.3s ease"]');
        if (icon) icon.classList.add('button-icon');
      });
      
      const emergencyBtn = document.querySelector('[style*="linear-gradient(135deg, #27ae60 0%, #219653 100%)"]');
      if (emergencyBtn) emergencyBtn.classList.add('emergency-btn');
      
      const dots = document.querySelectorAll('[style*="width: 6px"]');
      dots.forEach(dot => dot.classList.add('dot'));
      
      const bottomCTA = document.querySelector('[style*="linear-gradient(135deg, #1a2980 0%, #26d0ce 100%)"]');
      if (bottomCTA) bottomCTA.classList.add('bottom-cta');
    }, 100);
  }
};

// Inject styles and add class names
if (typeof document !== 'undefined') {
  document.head.appendChild(styleElement);
  addClassNames();
}